import React from "react";
import {
  Animated,
  View,
  Text,
  Pressable,
  Image,
  Linking,
  StyleSheet,
} from "react-native";
import BluePhone from "@assets/svg/BluePhone";
import CallWhatsapp from "@assets/images/CallWhatsapp.png";
import analytics from "@services/analytics";
import { MAP_VIEW_EVENTS } from "@constants/analyticsEventsCT";
import { setItem } from "@lib/storage";
import { STORAGE_KEY } from "./constants";

interface PanelProps {
  isPanelVisible: boolean;
  slideAnim: Animated.Value;
  contentHeight: number;
  pan: Animated.ValueXY;
  panResponder: any;
  selectedMarker: any; // Update this type as needed
  onContentLayout: (event: any) => void;
  setContactOption: (option: any) => void;
  contactOpts: string[];
  createCall: (
    phone: string,
    cx_id?: string,
    route_id?: string,
    callmatic_mode?: string,
  ) => Promise<void>;
}

const DraggableBar = ({ panResponder }: { panResponder: any }) => (
  <Animated.View style={styles.draggableBar} {...panResponder.panHandlers} />
);

const UserInfo = ({ selectedMarker }: { selectedMarker: any }) => (
  <View style={styles.userInfoContainer}>
    <Text style={styles.userInitial}>{selectedMarker?.user_name[0]}</Text>
    <Text style={styles.userName}>{selectedMarker?.user_name}</Text>
  </View>
);

const ContactActions = ({
  selectedMarker,
  setContactOption,
  contactOpts,
  createCall,
}: {
  selectedMarker: any;
  setContactOption: (option: string | null) => void;
  contactOpts: string[];
  createCall: (
    phone: string,
    cx_id?: string,
    route_id?: string,
    callmatic_mode?: string,
  ) => Promise<void>;
}) => {
  const onPressCall = () => {
    if (selectedMarker?.phone_number_1 && selectedMarker.phone_number_2) {
      setContactOption("phone");
    } else {
      if (selectedMarker?.user_id) {
        if (
          ["DELIVERED", "RTO_CAN_CANCEL", "RTO_CANNOT_CANCEL"].includes(
            selectedMarker.card_type,
          ) ||
          selectedMarker?.action
        ) {
          createCall(
            selectedMarker?.phone_number_1,
            selectedMarker?.user_id,
            selectedMarker?.route_id,
            selectedMarker?.callmatic_mode,
          );
          return;
        }
        analytics.trackEvent(MAP_VIEW_EVENTS.MAP_CALL_BTN_PRESS, {
          cx_id: selectedMarker.user_id,
        });
        setItem(
          STORAGE_KEY,
          JSON.stringify({
            cx_id: selectedMarker.user_id,
            action: "phone",
            cx_phone: selectedMarker?.phone_number_1,
            created_at: new Date(),
          }),
        ).then(() => {
          createCall(
            selectedMarker?.phone_number_1,
            selectedMarker?.user_id,
            selectedMarker?.route_id,
            selectedMarker?.callmatic_mode,
          );
        });
      } else {
        createCall(
          selectedMarker?.phone_number_1,
          selectedMarker?.user_id,
          selectedMarker?.route_id,
          selectedMarker?.callmatic_mode,
        );
      }
    }
  };

  const onPressWhatsapp = async () => {
    if (selectedMarker?.phone_number_1 && selectedMarker.phone_number_2) {
      setContactOption("wa");
    } else {
      try {
        if (selectedMarker?.user_id) {
          if (
            ["DELIVERED", "RTO_CAN_CANCEL", "RTO_CANNOT_CANCEL"].includes(
              selectedMarker.card_type,
            ) ||
            selectedMarker?.action
          ) {
            const url = `whatsapp://send?phone=+91${selectedMarker?.phone_number_1}`;
            Linking.canOpenURL(url).then(await Linking.openURL(url));
            return;
          }
          analytics.trackEvent(MAP_VIEW_EVENTS.MAP_WHATSAPP_BTN_PRESS, {
            cx_id: selectedMarker.user_id,
          });
          await setItem(
            STORAGE_KEY,
            JSON.stringify({
              cx_id: selectedMarker.user_id,
              action: "wa",
              cx_phone: selectedMarker?.phone_number_1,
              created_at: new Date(),
            }),
          );
          const url = `whatsapp://send?phone=+91${selectedMarker?.phone_number_1}`;
          Linking.canOpenURL(url).then(await Linking.openURL(url));
        } else {
          const url = `whatsapp://send?phone=+91${selectedMarker?.phone_number_1}`;
          Linking.canOpenURL(url).then(await Linking.openURL(url));
        }
      } catch (err) {
        Linking.openURL("market://details?id=com.whatsapp");
      }
    }
  };

  return (
    <View style={styles.actionsContainer}>
      {contactOpts.includes("DIALER") && (
        <Pressable onPress={onPressCall}>
          <BluePhone />
        </Pressable>
      )}
      {contactOpts.includes("WA") && (
        <Pressable onPress={onPressWhatsapp}>
          <Image source={CallWhatsapp} style={styles.whatsappIcon} />
        </Pressable>
      )}
    </View>
  );
};

const RouteInfo = ({ route_name }: { route_name: string }) => (
  <View>
    <Text style={styles.routeText}>
      Route : <Text style={styles.boldText}>{route_name}</Text>
    </Text>
  </View>
);

const AddressInfo = ({
  address,
  cx_id,
  cx_lat,
  cx_lng,
}: {
  address: string;
  cx_id: string;
  cx_lat: string;
  cx_lng: string;
}) => (
  <>
    <View>
      <Text style={styles.addressLabel}>Address :</Text>
    </View>
    <Pressable
      style={styles.addressContainer}
      onPress={() => {
        if (cx_id) {
          analytics.trackEvent(MAP_VIEW_EVENTS.MAP_ADDRESS_PRESS, { cx_id });
        }
        Linking.openURL(
          `https://www.google.com/maps/dir/?api=1&destination=${cx_lat},${cx_lng}`,
        );
      }}
    >
      <Text style={styles.addressText}>{address}</Text>
    </Pressable>
  </>
);

const PanelContent = ({
  selectedMarker,
  panResponder,
  onContentLayout,
  setContactOption,
  contactOpts,
  createCall,
}: {
  selectedMarker: any;
  panResponder: any;
  onContentLayout: (event: any) => void;
  setContactOption: (option: string | null) => void;
  contactOpts: string[];
  createCall: (
    phone: string,
    cx_id?: string,
    route_id?: string,
    callmatic_mode?: string,
  ) => Promise<void>;
}) => (
  <View onLayout={onContentLayout} style={styles.contentLayout}>
    <DraggableBar panResponder={panResponder} />
    <View style={styles.headerContainer}>
      <UserInfo selectedMarker={selectedMarker} />
      <ContactActions
        selectedMarker={selectedMarker}
        setContactOption={setContactOption}
        contactOpts={contactOpts}
        createCall={createCall}
      />
    </View>
    <View style={styles.divider} />
    {selectedMarker?.route_name && (
      <RouteInfo route_name={selectedMarker.route_name} />
    )}
    <AddressInfo
      address={selectedMarker?.cx_formatted_address}
      cx_id={selectedMarker?.user_id}
      cx_lat={selectedMarker?.cx_lat}
      cx_lng={selectedMarker?.cx_lng}
    />
  </View>
);

const AnimatedPanel = ({
  isPanelVisible,
  slideAnim,
  contentHeight,
  pan,
  selectedMarker,
  panResponder,
  onContentLayout,
  setContactOption,
  contactOpts,
  createCall,
}: PanelProps) => {
  if (!isPanelVisible) return null;

  return (
    <Animated.View
      style={[
        styles.panel,
        {
          transform: [
            {
              translateY: slideAnim.interpolate({
                inputRange: [0, 1],
                outputRange: [contentHeight, 0],
              }),
            },
            { translateY: pan.y },
          ],
        },
      ]}
    >
      <PanelContent
        selectedMarker={selectedMarker}
        panResponder={panResponder}
        onContentLayout={onContentLayout}
        setContactOption={setContactOption}
        contactOpts={contactOpts}
        createCall={createCall}
      />
    </Animated.View>
  );
};

export default AnimatedPanel;

const styles = StyleSheet.create({
  panel: {
    position: "absolute",
    bottom: -30,
    backgroundColor: "#fff",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    justifyContent: "center",
    alignItems: "center",
    elevation: 5,
  },
  contentLayout: {
    position: "relative",
    top: -10,
  },
  draggableBar: {
    width: 60,
    height: 7,
    backgroundColor: "#ccc",
    borderRadius: 2.5,
    alignSelf: "center",
    marginBottom: 10,
  },
  headerContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  userInfoContainer: {
    flexDirection: "row",
    alignItems: "center",
    maxWidth: "50%",
  },
  userInitial: {
    fontSize: 18,
    backgroundColor: "rgba(246, 246, 246, 1)",
    paddingHorizontal: 20,
    paddingVertical: 13,
    borderRadius: 50,
  },
  userName: {
    fontSize: 18,
    marginLeft: 20,
    fontWeight: "bold",
  },
  actionsContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  whatsappIcon: {
    height: 40,
    width: 40,
    marginLeft: 20,
  },
  divider: {
    width: "90%",
    height: 1,
    backgroundColor: "rgba(244, 244, 244, 1)",
    borderRadius: 2.5,
    alignSelf: "center",
    marginVertical: 20,
  },
  routeText: {
    fontSize: 16,
  },
  boldText: {
    fontWeight: "bold",
  },
  addressLabel: {
    fontSize: 16,
    fontWeight: "bold",
    marginTop: 20,
  },
  addressContainer: {
    marginBottom: 40,
  },
  addressText: {
    color: "rgba(66, 116, 234, 1)",
    textDecorationLine: "underline",
    marginTop: 10,
  },
});
